import { useState } from "react";
import { Shield, HelpCircle } from "lucide-react";

/**
 * ConfidenceBadge — Score de confiance + explicabilité, réutilisable partout
 * où Noam produit une projection, un score ou une prédiction. C'est la
 * signature « je te montre ce qui pourrait m'invalider » qui différencie
 * un outil d'exécutif d'un modèle statistique boîte noire.
 *
 * value        : 0-100
 * rationale    : texte court expliquant sur quoi la confiance est fondée
 * assumptions  : liste courte d'hypothèses qui rendent le score valide
 * caveats      : liste courte de ce qui pourrait l'invalider
 */
export default function ConfidenceBadge({
  value,
  rationale = "",
  assumptions = [],
  caveats = [],
  size = "sm",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const v = Math.max(0, Math.min(100, Number(value ?? 0)));

  const cfg = v >= 70
    ? { color: "text-[#1D9E75]", bg: "bg-[#1D9E75]/10 border-[#1D9E75]/30", label: "élevée", tone: "solide" }
    : v >= 45
      ? { color: "text-[#EF9F27]", bg: "bg-[#EF9F27]/10 border-[#EF9F27]/30", label: "moyenne", tone: "à valider" }
      : { color: "text-[#E24B4A]", bg: "bg-[#E24B4A]/10 border-[#E24B4A]/30", label: "faible", tone: "hypothétique" };

  const sizeCls = size === "lg" ? "text-xs px-2.5 py-1" : "text-[10px] px-1.5 py-0.5";
  const hasDetails = rationale || (assumptions?.length || 0) > 0 || (caveats?.length || 0) > 0;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 rounded border font-semibold ${sizeCls} ${cfg.color} ${cfg.bg}`} data-testid="confidence-badge-compact">
        <Shield className="w-3 h-3" /> {v}%
      </span>
    );
  }

  return (
    <div className="relative inline-block" data-testid="confidence-badge">
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded border font-semibold ${sizeCls} ${cfg.color} ${cfg.bg} ${hasDetails ? "cursor-pointer hover:brightness-125" : "cursor-default"}`}
        title={hasDetails ? "Cliquer pour voir sur quoi cette confiance est fondée" : ""}
      >
        <Shield className="w-3 h-3 flex-shrink-0" />
        Confiance {v}% · <span className="opacity-80">{cfg.label}</span>
        {hasDetails && <HelpCircle className="w-3 h-3 flex-shrink-0 opacity-60" />}
      </button>

      {open && hasDetails && (
        <div
          className="absolute z-30 mt-1.5 right-0 w-72 rounded-lg border border-white/10 bg-[#0A0A0F] p-3 shadow-2xl space-y-2"
          onClick={(e) => e.stopPropagation()}
          data-testid="confidence-badge-details"
        >
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${cfg.color}`}>
            Fondement de la confiance ({cfg.tone})
          </div>
          {rationale && (
            <p className="text-[11px] text-zinc-300 leading-snug">{rationale}</p>
          )}
          {assumptions?.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-1">Hypothèses tenues</div>
              <ul className="space-y-0.5">
                {assumptions.map((a, i) => (
                  <li key={i} className="text-[10.5px] text-zinc-300 leading-snug flex gap-1"><span className="text-[#1D9E75]">✓</span><span>{a}</span></li>
                ))}
              </ul>
            </div>
          )}
          {caveats?.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#E24B4A] font-semibold mb-1">Ce qui l'invaliderait</div>
              <ul className="space-y-0.5">
                {caveats.map((c, i) => (
                  <li key={i} className="text-[10.5px] text-zinc-300 leading-snug flex gap-1"><span className="text-[#E24B4A]">✗</span><span>{c}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
